import type { NextPage } from 'next';
import React from 'react';
import { FdrTextBlock } from '@hotelplan/fdr.regular.basis.fdr-text-block';
import { FdrCatalogs } from '@hotelplan/fdr.regular.fusion.fdr-catalogs';
import { FdrCatalogsOrderButtonVariant } from '@hotelplan/fdr.regular.fusion.fdr-catalogs-order-control';
import { FdrCatalogsOrderLayoutModalVariant } from '@hotelplan/fdr.regular.fusion.fdr-catalogs-order-layout';
import {
  FdrCatalogOrderInput,
  FdrNewsletterStatus,
} from '@hotelplan/supergraph-api';
import { FdrCatalogsContainer } from 'components/domain/fdr-catalogs/fdr-catalogs.styles';
import { FdrHeroSection } from 'components/domain/fdr-hero-section';
import { FdrPageComponents } from 'components/domain/fdr-page-components';
import { TextBlockContainer } from 'components/domain/text-block/TextBlockContainer';
import { useFdrCatalogsOrderMutation } from 'schemas/mutation/catalogs/fdr-catalogs-order.generated';
import { useFdrCreateNewsletterSubscriptionMutation } from 'schemas/mutation/newsletter/subscription/fdr-create-newsletter-subscription.generated';
import { FdrCatalogsPageComponentsQuery } from 'schemas/query/catalogs/fdr-catalogs-page-components.generated';
import { FdrCatalogsPageContentQuery } from 'schemas/query/catalogs/fdr-catalogs-page-content.generated';
import { FdrCatalogsQuery } from 'schemas/query/catalogs/fdr-catalogs.generated';

export { getServerSideProps } from 'components/page/catalogs/catalogs.gssp';

interface ICatalogsPageProps {
  content: FdrCatalogsPageContentQuery['fdrCatalogPage'];
  pageComponents: FdrCatalogsPageComponentsQuery['fdrCatalogPage']['pageComponents'];
  catalogsList: FdrCatalogsQuery['fdrCatalogs'];
}

const CatalogsOverviewPage: NextPage<ICatalogsPageProps> = ({
  content,
  pageComponents,
  catalogsList,
}) => {
  const { hero, flagline, title, text } = content || {};

  const [fdrOrderCatalogsMutation] = useFdrCatalogsOrderMutation();
  const [createSubscription] = useFdrCreateNewsletterSubscriptionMutation();

  async function handleOrderSubmit(
    orderInput: FdrCatalogOrderInput,
    newsletterSubscribe: boolean
  ): Promise<boolean> {
    const { data: orderData } = await fdrOrderCatalogsMutation({
      variables: {
        input: orderInput,
      },
    });

    if (newsletterSubscribe && orderData?.fdrCatalogOrder?.success) {
      const { data: subscriptionData } = await createSubscription({
        variables: {
          input: {
            email: orderInput.customer.email,
            firstName: orderInput.customer.firstName,
            lastName: orderInput.customer.lastName,
            salutation: orderInput.customer.salutation,
          },
        },
      });

      return (
        subscriptionData?.fdrNewsletterSubscription?.result?.status ===
        FdrNewsletterStatus.Success
      );
    }

    return orderData?.fdrCatalogOrder?.success;
  }

  return (
    <>
      <FdrHeroSection hero={hero} title={title} text={flagline} />
      <TextBlockContainer>
        <FdrTextBlock text={text} />
      </TextBlockContainer>
      <FdrCatalogsContainer>
        <FdrCatalogs
          onSubmit={handleOrderSubmit}
          catalogs={catalogsList?.catalogs}
          isCaptchaEnabled={true}
          cardConfig={{
            orderComponent: FdrCatalogsOrderButtonVariant,
            showValidityDates: false,
            showViewOnlineButton: false,
          }}
          orderConfig={{
            layoutComponent: FdrCatalogsOrderLayoutModalVariant,
            isCounterEnabled: true,
            showInlinesErrorMessages: false,
            isDropdownEmptyValueEnabled: false,
          }}
        />
      </FdrCatalogsContainer>
      <FdrPageComponents components={pageComponents?.components} />
    </>
  );
};

export default CatalogsOverviewPage;
